import { pageViewTypes } from "@constants/tracking";
import {
	defaultFeedbackSubtitle,
	dialogTexts,
} from "@features/feedback/constants";
import { useFeedbackParams } from "@features/feedback/hooks/useFeedbackParams";
import useAnalytics from "@hooks/useAnalytics";
import { useSendFeedback } from "@memorang/features/src/feedback/hooks/useSendFeedback";
import { getFeedbackReasonsByType } from "@memorang/helpers/feedback";
import { isIOS } from "@memorang/helpers/platform";
import Box from "@memorang/ui/components/Box";
import Button from "@memorang/ui/components/Button";
import { FeedbackContent } from "@memorang/ui/components/FeedbackDialog/FeedbackContent";
import ModalHeader from "@memorang/ui/components/ModalHeader";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import { useToast } from "@memorang/ui/components/Toast";
import type { IconType } from "@memorang/ui/icon";
/* -----------------Globals--------------- */
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Keyboard, KeyboardAvoidingView, type ScrollView } from "react-native";

const FeedbackRoute = () => {
	useAnalytics({
		type: pageViewTypes.feedback,
	});

	const {
		view,
		itemId,
		sessionId,
		threadId,
		runId,
		type,
		featureName,
		feedbackCta,
		feedbackTitle,
		feedbackSubtitle: feedbackSubtitleParam,
		itemFeedbackRating,
		placeholder,
		feedbackReasonType,
	} = useLocalSearchParams<{
		view?: string;
		itemId?: string;
		sessionId?: string;
		threadId?: string;
		runId?: string;
		type?: string;
		featureName?: string;
		feedbackCta?: string;
		feedbackTitle?: string;
		feedbackSubtitle?: string;
		feedbackIcon?: string;
		itemFeedbackRating?: string;
		placeholder?: string;
		feedbackReasonType?: string;
	}>();
	const router = useRouter();
	const { showToast } = useToast();
	const feedbackContentRef = useRef<{ submit: () => void }>(null);
	const scrollViewRef = useRef<ScrollView>(null);
	const focusedFieldRef = useRef<"email" | "feedback" | null>(null);
	const [sendingFeedback, setSendingFeedback] = useState(false);
	const [isValid, setIsValid] = useState(false);

	const revealFocusedField = useCallback(() => {
		if (focusedFieldRef.current === "feedback") {
			scrollViewRef.current?.scrollToEnd({ animated: true });
		} else if (focusedFieldRef.current === "email") {
			scrollViewRef.current?.scrollTo({ y: 0, animated: true });
		}
	}, []);

	const handleInputFocus = useCallback(
		(field: "email" | "feedback") => {
			focusedFieldRef.current = field;
			requestAnimationFrame(revealFocusedField);
		},
		[revealFocusedField],
	);

	useEffect(() => {
		const subscription = Keyboard.addListener(
			"keyboardDidShow",
			revealFocusedField,
		);
		return () => subscription.remove();
	}, [revealFocusedField]);

	const config = dialogTexts.feedback;
	const { t } = useTranslation();

	const feedbackDialogParams = useFeedbackParams({
		view: view || "profile",
		type: (type as "Review" | "Content Flagged") || undefined,
		itemId,
		sessionId,
		threadId,
		runId,
		rating: itemFeedbackRating,
		featureName,
	});

	const handleDismissFeedback = () => {
		router.back();
	};

	const sendFeedback = useSendFeedback();

	const finalTitle = feedbackTitle || t(config.title);
	const finalSubtitle = feedbackSubtitleParam || t(defaultFeedbackSubtitle);
	const finalCta = feedbackCta || t("dialog.feedback.cta");
	const finalPlaceholder = placeholder || t("dialog.feedback.placeholder");
	const finalSuccessMessage = t("feedback.successMessage");
	const finalLimitError = t(
		"report.sessionAnalysis.feedbackDialog.feedbackLimitError",
	);

	const feedbackReasonList = useMemo(
		() =>
			getFeedbackReasonsByType<IconType>(
				feedbackReasonType as "negative" | "report" | undefined,
				t,
			),
		[feedbackReasonType, t],
	);

	return (
		<>
			<Stack.Screen
				options={{
					presentation: "modal",
					headerShown: true,
					header: () => (
						<ModalHeader
							backIcon="arrow-left"
							headerTitle={finalTitle}
							onBack={handleDismissFeedback}
						/>
					),
				}}
			/>
			<KeyboardAvoidingView
				style={{ flex: 1 }}
				behavior={isIOS ? "padding" : undefined}
			>
				<ScrollContainer
					ref={scrollViewRef}
					keyboardShouldPersistTaps="handled"
					keyboardDismissMode="interactive"
				>
					<Box paddingTop={16}>
						<FeedbackContent
							ref={feedbackContentRef}
							sendFeedback={sendFeedback}
							feedbackDialogParams={feedbackDialogParams}
							onDismiss={handleDismissFeedback}
							subtitle={finalSubtitle}
							feedbackPlaceholder={finalPlaceholder}
							feedbackLimitError={finalLimitError}
							feedbackReasonList={feedbackReasonList}
							showFeedbackConfirmationToast={() =>
								showToast({
									message: finalSuccessMessage,
									duration: 3_000,
								})
							}
							onStateChange={(sending, valid) => {
								setSendingFeedback(sending);
								setIsValid(valid);
							}}
							onInputFocus={handleInputFocus}
						/>
					</Box>
					<Box paddingHorizontal={24} paddingVertical={16}>
						<Button
							onPress={() => feedbackContentRef.current?.submit()}
							loading={sendingFeedback}
							disabled={!isValid}
							mode="contained"
						>
							{finalCta}
						</Button>
					</Box>
				</ScrollContainer>
			</KeyboardAvoidingView>
		</>
	);
};

export default FeedbackRoute;
